import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  History, 
  FileImage, 
  FlaskConical,
  Clock, 
  Trash2, 
  ChevronRight,
  Search,
  Loader2,
  AlertCircle,
  Scan
} from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Skeleton } from "../../components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";
import { LabsLayout } from "../../components/LabsLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HistoryPage() {
  const navigate = useNavigate();
  const [interpretations, setInterpretations] = useState([]);
  const [labReports, setLabReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const [imagingRes, labRes] = await Promise.all([
        axios.get(`${API}/labs/interpretations`, { withCredentials: true }),
        axios.get(`${API}/labs/lab-reports`, { withCredentials: true })
      ]);
      setInterpretations(imagingRes.data);
      setLabReports(labRes.data);
    } catch (error) {
      console.error("Error fetching history:", error);
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteItem) return;
    
    setDeleting(true);
    try {
      const endpoint = deleteItem.type === 'lab' 
        ? `${API}/labs/lab-reports/${deleteItem.id}`
        : `${API}/labs/interpretations/${deleteItem.id}`;
      
      await axios.delete(endpoint, { withCredentials: true });
      
      if (deleteItem.type === 'lab') {
        setLabReports(prev => prev.filter(item => item.id !== deleteItem.id));
      } else {
        setInterpretations(prev => prev.filter(item => item.id !== deleteItem.id));
      }
      
      toast.success("Analysis deleted");
    } catch (error) {
      console.error("Error deleting:", error);
      toast.error("Failed to delete analysis");
    } finally {
      setDeleting(false);
      setDeleteItem(null);
    }
  };

  const filterItems = (items, type) => {
    const query = searchQuery.toLowerCase();
    return items.filter(item => {
      if (type === 'imaging') {
        return (
          item.file_name?.toLowerCase().includes(query) ||
          item.scan_type?.toLowerCase().includes(query) ||
          item.impression?.toLowerCase().includes(query)
        );
      } else {
        return (
          item.file_name?.toLowerCase().includes(query) ||
          item.report_type?.toLowerCase().includes(query) ||
          item.clinical_interpretation?.toLowerCase().includes(query)
        );
      }
    });
  };

  const filteredImaging = filterItems(interpretations, 'imaging');
  const filteredLab = filterItems(labReports, 'lab');
  
  const allItems = [
    ...filteredImaging.map(i => ({ ...i, itemType: 'imaging' })),
    ...filteredLab.map(l => ({ ...l, itemType: 'lab' }))
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const getDisplayItems = () => {
    switch (activeTab) {
      case 'imaging':
        return filteredImaging.map(i => ({ ...i, itemType: 'imaging' }));
      case 'lab':
        return filteredLab.map(l => ({ ...l, itemType: 'lab' }));
      default:
        return allItems;
    }
  };

  const displayItems = getDisplayItems();

  return (
    <LabsLayout>
      <div className="min-h-[calc(100vh-4rem)] py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-blue-600/10 flex items-center justify-center">
                <History className="w-5 h-5 text-blue-500" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Analysis History
              </h1>
            </div>
            <p className="text-zinc-400 ml-13">
              View and manage your past medical analyses
            </p>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
            <TabsList className="bg-zinc-900 border border-zinc-800">
              <TabsTrigger 
                value="all" 
                data-testid="tab-all"
                className="data-[state=active]:bg-zinc-800"
              >
                All ({interpretations.length + labReports.length})
              </TabsTrigger>
              <TabsTrigger 
                value="imaging" 
                data-testid="tab-imaging"
                className="data-[state=active]:bg-zinc-800"
              >
                <Scan className="w-4 h-4 mr-2" />
                Imaging ({interpretations.length})
              </TabsTrigger>
              <TabsTrigger 
                value="lab" 
                data-testid="tab-lab"
                className="data-[state=active]:bg-zinc-800"
              >
                <FlaskConical className="w-4 h-4 mr-2" />
                Lab Reports ({labReports.length})
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Search */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <Input
                type="text"
                placeholder="Search by filename, type, or interpretation..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-input"
                className="pl-10 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-500 focus:border-blue-600"
              />
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="w-full h-32 rounded-lg bg-zinc-800" />
              ))}
            </div>
          ) : displayItems.length === 0 ? (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center mx-auto mb-4">
                  <FileImage className="w-8 h-8 text-zinc-600" />
                </div>
                <h3 className="text-lg font-medium text-white mb-2">
                  {searchQuery ? "No matching results" : "No analyses yet"}
                </h3>
                <p className="text-zinc-500 mb-6">
                  {searchQuery 
                    ? "Try adjusting your search terms" 
                    : "Upload a medical image or lab report to get started"}
                </p>
                {!searchQuery && (
                  <div className="flex gap-4 justify-center">
                    <Button 
                      onClick={() => navigate("/labs")}
                      data-testid="start-imaging-btn"
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Scan className="w-4 h-4 mr-2" />
                      Analyze Image
                    </Button>
                    <Button 
                      onClick={() => navigate("/labs/lab-reports")}
                      data-testid="start-lab-btn"
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <FlaskConical className="w-4 h-4 mr-2" />
                      Analyze Lab Report
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4" data-testid="history-list">
              {displayItems.map((item, index) => (
                <HistoryCard
                  key={item.id}
                  item={item}
                  index={index}
                  onDelete={() => setDeleteItem({ id: item.id, type: item.itemType })}
                  onClick={() => navigate(
                    item.itemType === 'lab' 
                      ? `/labs/lab-report/${item.id}` 
                      : `/labs/analysis/${item.id}`
                  )}
                />
              ))}
            </div>
          )}

          {/* Stats */}
          {!loading && (interpretations.length > 0 || labReports.length > 0) && (
            <div className="mt-8 p-4 bg-zinc-900/30 rounded-lg border border-zinc-800">
              <p className="text-sm text-zinc-500 text-center">
                Showing {displayItems.length} of {interpretations.length + labReports.length} analyses
              </p>
            </div>
          )}
        </div>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deleteItem} onOpenChange={() => setDeleteItem(null)}>
          <AlertDialogContent className="bg-zinc-900 border-zinc-800">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-500" />
                Delete Analysis
              </AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                Are you sure you want to delete this {deleteItem?.type === 'lab' ? 'lab report' : 'imaging'} analysis? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel 
                className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700"
                data-testid="cancel-delete-btn"
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleting}
                data-testid="confirm-delete-btn"
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  "Delete"
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </LabsLayout>
  );
}

const HistoryCard = ({ item, index, onDelete, onClick }) => {
  const isLab = item.itemType === 'lab';
  
  return (
    <Card
      className="bg-zinc-900/50 border-zinc-800 cursor-pointer overflow-hidden"
      onClick={onClick}
      data-testid={`history-item-${item.id}`}
    >
      <CardContent className="p-0">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
              isLab ? 'bg-emerald-500/10' : 'bg-blue-500/10'
            }`}>
              {isLab ? (
                <FlaskConical className="w-6 h-6 text-emerald-500" />
              ) : (
                <FileImage className="w-6 h-6 text-blue-500" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1 flex-wrap">
                <h3 className="font-medium text-white truncate">
                  {item.file_name}
                </h3>
                <Badge 
                  variant="secondary" 
                  className={`flex-shrink-0 ${
                    isLab 
                      ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' 
                      : 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                  }`}
                >
                  {isLab ? (item.report_type || "Lab Report") : (item.scan_type || "Imaging")}
                </Badge>
              </div>
              <p className="text-sm text-zinc-500 line-clamp-1">
                {isLab ? item.clinical_interpretation : item.impression || "No interpretation available"}
              </p>
              <div className="flex items-center gap-1 mt-2 text-xs text-zinc-600">
                <Clock className="w-3 h-3" />
                {new Date(item.created_at).toLocaleString()}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              data-testid={`delete-btn-${item.id}`}
              className="text-zinc-500 hover:text-red-400 hover:bg-red-950/30"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
            <ChevronRight className="w-5 h-5 text-zinc-600" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
